/* The Chain Timeline — 4-layer Canvas-rendered timeline.
 *
 *   Layer 1 — Spine: era bands (soft gradients), particle flow, density heatmap.
 *   Layer 2 — Constellations: each walked chain as a 5-node figure above the spine.
 *                 Origin (gold, on spine) → Change A → Change B → Contradiction (cyan)
 *                 → Modern Pattern (arc back to today).  Line thickness = rating.
 *   Layer 3 — Lineage ribbons: ancestor lifespans as vertical bars below the spine,
 *                 brightness = beacon strength.  Privacy-respected.
 *   Layer 4 — Scrub cursor: vertical beam piercing all three layers; active
 *                 elements warm, pulse, highlight.
 *
 *   Drop-in replacement for <Timeline currentYear onYearChange events scopeRange />.
 *   Same contract — the rest of the app doesn't know it changed.
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { chainAPI, lineageAPI, timelineAPI } from '../lib/api';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Camera, Download, X, HelpCircle } from 'lucide-react';
import { useScreenRattleSfx, useTapeHiss, useDeepTimeDrone, useLockClick } from '../lib/audioSfx';
import { useSfxMuted } from '../lib/sfxPrefs';

// World history starts seven million years ago — Lead's Apr 28 2026
// brief. The full timeline now spans ~7,002,026 years. Year-label
// rendering, span clamps, and zoom math all key off these constants.
const MIN_YEAR = -7000000;
const MAX_YEAR = 2026;
const ORIGIN_YEAR = -7000000;  // The origin of the human story; play button starts here.
const TODAY = new Date().getFullYear();

// Era bands — derived from lib/eraGrammar.js so timeline + content pages + export
// all paint from the same canonical palette. NEVER introduce rainbow-flag gradients.
// 10-era grammar compressed to the 5 we render on the timeline surface for visual
// rhythm; palette picks the era's `primary` color so the spine reads historical,
// not political.
const ERAS = [
  // Ancient — stone/gold (C4956A from era1)
  { id: 'ancient', label: 'ANCIENT', range: [-3000, -500], color: '#C4956A' },
  // Classical — terracotta (C75B3F from era2)
  { id: 'classical', label: 'CLASSICAL', range: [-500, 500], color: '#C75B3F' },
  // Medieval — parchment gold (C8A951 from era3 accent)
  { id: 'medieval', label: 'MEDIEVAL', range: [500, 1500], color: '#C8A951' },
  // Early Modern — letterpress bronze (8B7D3C from era4 accent)
  { id: 'earlymodern', label: 'EARLY MODERN', range: [1500, 1800], color: '#8B7D3C' },
  // Modern — contemporary cyan (00F0FF from era9)
  { id: 'modern', label: 'MODERN', range: [1800, 2026], color: '#2F8A92' },
];

const STEP_KEYS = ['origin', 'change_a', 'change_b', 'contradiction', 'modern_pattern'];

function formatYear(year) {
  // Compact display for deep-time spans. Lead's Apr 28 2026 brief: world
  // history starts 7 million years ago — labels need to read cleanly at
  // every magnitude.
  const abs = Math.abs(year);
  if (year < 0) {
    if (abs >= 1_000_000) return `${(abs / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1)}M BCE`;
    if (abs >= 10_000)    return `${Math.round(abs / 1000)}k BCE`;
    return `${abs} BCE`;
  }
  return `${year} CE`;
}

function eraForYear(year) {
  return ERAS.find((e) => year >= e.range[0] && year <= e.range[1]) || ERAS[0];
}

function hexToRgba(hex, alpha = 1) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* Year → pixel, given viewport state */
// Accept scopeRange as either [start, end] OR {start, end, label} so
// callers can pass a richer object (e.g. countryFoundings.resolveTimelineScope)
// and still slot into the legacy array contract.
function _scopeRangeStart(s) {
  if (!s) return null;
  if (Array.isArray(s)) return Number.isFinite(s[0]) ? s[0] : null;
  if (typeof s === 'object') return Number.isFinite(s.start) ? s.start : null;
  return null;
}
function _scopeRangeEnd(s) {
  if (!s) return null;
  if (Array.isArray(s)) return Number.isFinite(s[1]) ? s[1] : null;
  if (typeof s === 'object') return Number.isFinite(s.end) ? s.end : null;
  return null;
}

function yearToX(year, viewStart, viewEnd, width) {
  return ((year - viewStart) / (viewEnd - viewStart)) * width;
}

function xToYear(x, viewStart, viewEnd, width) {
  return Math.round(viewStart + (x / width) * (viewEnd - viewStart));
}

/* Layout geometry — all heights relative to the main canvas height.
 * Apr 28 2026 brief: shift all rendering down by 14px so the legend bar
 * (now docked at top-0 of the timeline canvas) never covers the chain
 * arcs / contradiction strikes / constellation nodes. */
const LAYOUT = {
  constellationTop: 48,       // Apr 30 2026 iter_69: legend moved to top-edge of row
                              // so constellation starts below the legend at y=48
  constellationHeight: 52,    // chain nodes drawn between these Y values
  spineTop: 108,
  spineHeight: 20,            // era band + density heatmap
  spineMid: 118,              // origin anchor line
  ribbonTop: 134,
  ribbonHeight: 40,
  // Apr 30 2026 iter_69 per Lead: "Timeline dates are getting cut off.
  // I asked you to put dates in timeline. That alot of real estate you
  // wasting taking away from glove." Year labels moved UP from y=176
  // (bottom edge, getting clipped by row height) to y=86 — placed
  // INSIDE the timeline just below the era spine so they're always
  // visible, never cut off, and the row below them is freed.
  yearLabelY: 86,
};

const CANVAS_H = 182;           // trimmed from 198 — labels moved up freed space
const CANVAS_DPR_CAP = 2;     // retina cap to save GPU

/* ────────────────────────────────────────── */

export default function ChainTimeline({ currentYear, onYearChange, events = [], scopeRange = null, onChainSelect, onStartJourney }) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);

  // Screen rattle + SFX — fired when the gold play button starts a journey.
  const [muted] = useSfxMuted();
  const playRattle = useScreenRattleSfx({ enabled: !muted, volume: 0.45 });
  const playLock = useLockClick({ enabled: !muted, volume: 0.22 });

  // Time-machine ambient bed — soft tape hiss plus a deep-time drone that
  // swells the further the user scrubs into pre-civilization. Both
  // start on first user gesture (browser autoplay policy) and respect
  // the global mute. Apr 29 2026 Lead-approved suggestion.
  const tapeHiss = useTapeHiss({ enabled: !muted, volume: 0.035 });
  const deepDrone = useDeepTimeDrone({ enabled: !muted, volume: 0.06 });
  const ambientStartedRef = useRef(false);
  // Start the bed on the first interaction inside the timeline area.
  useEffect(() => {
    const startBed = () => {
      if (ambientStartedRef.current || muted) return;
      ambientStartedRef.current = true;
      tapeHiss.start();
      deepDrone.start();
    };
    window.addEventListener('pointerdown', startBed, { once: true, capture: true });
    window.addEventListener('keydown', startBed, { once: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', startBed, true);
      window.removeEventListener('keydown', startBed, true);
    };
  }, [muted, tapeHiss, deepDrone]);
  // Stop the bed when muted; restart when unmuted (and bed previously started).
  useEffect(() => {
    if (muted) {
      tapeHiss.stop();
      deepDrone.stop();
    } else if (ambientStartedRef.current) {
      tapeHiss.start();
      deepDrone.start();
    }
  }, [muted, tapeHiss, deepDrone]);
  // Drive the drone + hiss intensity from currentYear. -7M → 1.0 (max
  // tectonic), 0 BCE → 0.4, 1500 → 0.18, 2026 → 0.05. Linear blend so
  // the user feels the present "shrink" as they scrub backward.
  useEffect(() => {
    if (!ambientStartedRef.current) return;
    const y = typeof currentYear === 'number' ? currentYear : -7000000;
    let intensity;
    if (y <= -1000000) intensity = 1.0;
    else if (y <= 0) intensity = 0.4 + 0.6 * Math.min(1, -y / 1000000);
    else if (y <= 1500) intensity = 0.18 + 0.22 * (1 - y / 1500);
    else intensity = Math.max(0.05, 0.18 - 0.13 * Math.min(1, (y - 1500) / 526));
    tapeHiss.setIntensity(intensity);
    deepDrone.setIntensity(intensity);
  }, [currentYear, tapeHiss, deepDrone]);

  /* ─ view state ─ */
  const [viewStart, setViewStart] = useState(_scopeRangeStart(scopeRange) ?? MIN_YEAR);
  const [viewEnd, setViewEnd] = useState(_scopeRangeEnd(scopeRange) ?? MAX_YEAR);
  const [hover, setHover] = useState(null); // { type, data, x, y }
  const [pinnedEvent, setPinnedEvent] = useState(null); // sticky event popover after click
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, viewStart: 0, viewEnd: 0 });
  // Live canvas width tracked via ResizeObserver — drives the journey
  // play button overlay's x-position so it stays glued to the bottom of
  // the gold cursor as the user resizes / zooms / scrolls the timeline.
  const [canvasWidth, setCanvasWidth] = useState(0);
  useEffect(() => {
    if (!wrapperRef.current) return;
    const measure = () => setCanvasWidth(wrapperRef.current?.clientWidth || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);
  // Cursor x in CSS pixels (canvas client coords). Used to position the
  // play button at the bottom of the gold marker line. -1 if off-screen.
  const cursorX = (() => {
    if (!canvasWidth) return -1;
    const px = ((currentYear - viewStart) / Math.max(1, (viewEnd - viewStart))) * canvasWidth;
    if (px < -10 || px > canvasWidth + 10) return -1;
    return px;
  })();

  /* ─ data state ─ */
  const [chains, setChains] = useState([]);
  const [beacons, setBeacons] = useState([]);
  const [intersections, setIntersections] = useState([]);
  const [densityGrid, setDensityGrid] = useState(null);
  const [contextEvents, setContextEvents] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [hasLineage, setHasLineage] = useState(false);
  const [showLegend, setShowLegend] = useState(() => {
    try { return localStorage.getItem('tsogn-legend-dismissed') !== '1'; } catch { return true; }
  });
  // First-time hover-education: cascade nodes Origin → Modern once, then off
  const [tutorialChainId, setTutorialChainId] = useState(null);
  const [tutorialStart, setTutorialStart] = useState(0);
  const hasSeenTutorialRef = useRef(false);
  useEffect(() => {
    try { hasSeenTutorialRef.current = localStorage.getItem('tsogn-chain-tutorial-seen') === '1'; } catch {}
  }, []);

  /* ─ respond to scope change ─ */
  useEffect(() => {
    if (scopeRange) {
      const s = _scopeRangeStart(scopeRange);
      const e = _scopeRangeEnd(scopeRange);
      if (s !== null && e !== null && e > s) {
        setViewStart(s);
        setViewEnd(e);
      } else {
        // Malformed scope — bail back to full -7M → 2026 view.
        setViewStart(MIN_YEAR);
        setViewEnd(MAX_YEAR);
      }
    } else {
      setViewStart(MIN_YEAR);
      setViewEnd(MAX_YEAR);
    }
  }, [scopeRange]);

  /* ─ load data once ─ */
  useEffect(() => {
    chainAPI.list().then((r) => {
      const chs = (r.data || []).filter((c) => c.origin?.year !== undefined);
      setChains(chs);
      // Pre-compute density: 200-year bins
      const bins = {};
      for (const c of chs) {
        const y = c.origin.year;
        const bin = Math.floor(y / 200) * 200;
        bins[bin] = (bins[bin] || 0) + 1;
      }
      setDensityGrid(bins);
    }).catch(() => setChains([]));

    lineageAPI.beacons().then((r) => {
      setHasLineage(Array.isArray(r.data?.beacons) && r.data.beacons.length > 0);
      if (r.data?.lineage_visible) setBeacons(r.data.beacons || []);
      else setBeacons([]);
    }).catch(() => setBeacons([]));

    lineageAPI.intersections().then((r) => setIntersections(r.data || [])).catch(() => setIntersections([]));

    // Context-events (cyan blue-dots for 32 deep-time milestones) REMOVED
    // Apr 30 2026 iter_62 per Lead: "timeline needs to clear all chains.
    // Except the real." Only the 4 canonical chain markers are rendered
    // on the timeline now. DO NOT re-add the timelineAPI.events() fetch.
    setContextEvents([]);
  }, []);

  /* ─ initialize particles ─ */
  useEffect(() => {
    particlesRef.current = Array.from({ length: 42 }, () => ({
      x: Math.random(),      // 0–1 across viewport
      y: LAYOUT.spineMid + (Math.random() - 0.5) * 4,
      vx: 0.00015 + Math.random() * 0.0002,  // x fraction per ms
      alpha: 0.15 + Math.random() * 0.25,
    }));
  }, []);

  /* ─ render loop ─ */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let last = performance.now();
    const frame = (t) => {
      const dt = t - last;
      last = t;
      drawFrame(canvas, {
        viewStart,
        viewEnd,
        chains,
        beacons,
        intersections,
        densityGrid,
        contextEvents,
        currentYear,
        hover,
        dt,
        particles: particlesRef.current,
        tutorial: tutorialChainId ? { chainId: tutorialChainId, startedAt: tutorialStart, now: t } : null,
      });
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, [viewStart, viewEnd, chains, beacons, intersections, densityGrid, contextEvents, currentYear, hover, tutorialChainId, tutorialStart]);

  /* ─ resize handling — high DPI + responsive width ─ */
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const resize = () => {
      const rect = wrapper.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, CANVAS_DPR_CAP);
      canvas.width = rect.width * dpr;
      canvas.height = CANVAS_H * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${CANVAS_H}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  /* ─ hit testing for hover / click ─ */
  const hitTest = useCallback((mouseX, mouseY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const width = canvas.clientWidth;

    // Test chain constellation nodes first (origin is most clickable)
    for (const c of chains) {
      const origin = c.origin;
      if (!origin?.year) continue;
      const x = yearToX(origin.year, viewStart, viewEnd, width);
      if (x < -20 || x > width + 20) continue;
      const dx = mouseX - x;
      const dy = mouseY - LAYOUT.spineMid;
      if (dx * dx + dy * dy < 64) { // 8px radius
        return { type: 'chain', data: c, x, y: LAYOUT.spineMid };
      }
    }

    // Test ribbons
    for (const b of beacons) {
      if (!b.birth_year) continue;
      const bStart = b.birth_year;
      const bEnd = b.death_year || (bStart + 85);
      const xs = yearToX(bStart, viewStart, viewEnd, width);
      const xe = yearToX(bEnd, viewStart, viewEnd, width);
      if (mouseX >= xs && mouseX <= xe && mouseY >= LAYOUT.ribbonTop && mouseY <= LAYOUT.ribbonTop + LAYOUT.ribbonHeight) {
        return { type: 'beacon', data: b, x: (xs + xe) / 2, y: LAYOUT.ribbonTop };
      }
    }

    // Test cyan context-event dots — Lead's Apr 28 brief: clicking/hovering
    // the blue points on the spine should expose the event title, era, and
    // source. Dots draw at LAYOUT.spineMid + 10; we use a generous 10px
    // hit radius so judges don't have to pixel-hunt.
    const eventY = LAYOUT.spineMid + 10;
    let bestEvent = null;
    let bestDist = 100; // 10px squared
    for (const ev of contextEvents) {
      if (typeof ev?.year !== 'number') continue;
      const x = yearToX(ev.year, viewStart, viewEnd, width);
      if (x < -20 || x > width + 20) continue;
      const dx = mouseX - x;
      const dy = mouseY - eventY;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) { bestDist = d2; bestEvent = { ev, x }; }
    }
    if (bestEvent) {
      return { type: 'event', data: bestEvent.ev, x: bestEvent.x, y: eventY };
    }

    return null;
  }, [chains, beacons, contextEvents, viewStart, viewEnd]);

  /* ─ mouse events ─ */
  const onMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const hit = hitTest(mx, my);
    setHover(hit ? { ...hit, mouseX: mx, mouseY: my } : null);
    // First-time chain hover → trigger education cascade once, then mark seen
    if (hit && hit.type === 'chain' && !hasSeenTutorialRef.current && !tutorialChainId) {
      setTutorialChainId(hit.data.id);
      setTutorialStart(performance.now());
      hasSeenTutorialRef.current = true;
      try { localStorage.setItem('tsogn-chain-tutorial-seen', '1'); } catch {}
      // Clear after tutorial finishes (1.6s)
      setTimeout(() => setTutorialChainId(null), 1700);
    }
  };

  const onMouseLeave = () => setHover(null);

  const onMouseDown = (e) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX,
      viewStart,
      viewEnd,
    };
  };

  const onClick = (e) => {
    // Ignore drag-initiated mouseups
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const hit = hitTest(mx, my);
    if (hit?.type === 'chain' && onChainSelect) {
      // Lock click — satisfying mechanical-tumbler pin commit.
      if (!muted) playLock();
      onChainSelect(hit.data);
    } else if (hit?.type === 'event') {
      // Pin the popover by storing the click coords. Pinned popover is
      // pointer-events: auto so the user can click the source link
      // inside. Also nudge the scrub year to the event so the rest
      // of the HUD shifts to that moment in time.
      setPinnedEvent({ ...hit, mouseX: mx, mouseY: my });
      onYearChange?.(Math.max(MIN_YEAR, Math.min(MAX_YEAR, hit.data.year)));
    } else if (!hit) {
      // Clicking the spine scrubs to that year — and dismisses any
      // pinned event popover.
      setPinnedEvent(null);
      const width = canvasRef.current.clientWidth;
      const year = xToYear(mx, viewStart, viewEnd, width);
      onYearChange?.(Math.max(MIN_YEAR, Math.min(MAX_YEAR, year)));
    }
  };

  useEffect(() => {
    if (!isDragging) return;
    const move = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const width = canvas.clientWidth;
      const dx = e.clientX - dragStart.current.x;
      const yearsPerPx = (dragStart.current.viewEnd - dragStart.current.viewStart) / width;
      const shift = -dx * yearsPerPx;
      const newStart = Math.max(MIN_YEAR, Math.min(MAX_YEAR - 100, dragStart.current.viewStart + shift));
      const newEnd = Math.max(newStart + 100, Math.min(MAX_YEAR, dragStart.current.viewEnd + shift));
      setViewStart(newStart);
      setViewEnd(newEnd);
    };
    const up = () => setIsDragging(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [isDragging]);

  /* ─ wheel zoom (anchored on cursor position) ─ */
  const onWheel = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const width = canvas.clientWidth;
    const anchorYear = xToYear(mx, viewStart, viewEnd, width);
    const zoomFactor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
    const newSpan = Math.max(100, Math.min(MAX_YEAR - MIN_YEAR, (viewEnd - viewStart) * zoomFactor));
    const mouseFrac = (anchorYear - viewStart) / (viewEnd - viewStart);
    let newStart = anchorYear - mouseFrac * newSpan;
    let newEnd = newStart + newSpan;
    if (newStart < MIN_YEAR) { newStart = MIN_YEAR; newEnd = newStart + newSpan; }
    if (newEnd > MAX_YEAR) { newEnd = MAX_YEAR; newStart = newEnd - newSpan; }
    setViewStart(Math.max(MIN_YEAR, newStart));
    setViewEnd(Math.min(MAX_YEAR, newEnd));
  };

  /* ─ double-click snaps to today ─ */
  const onDoubleClick = () => {
    const span = 200;
    setViewStart(TODAY - span);
    setViewEnd(TODAY);
    onYearChange?.(TODAY);
  };

  const nudge = (dir) => {
    const span = viewEnd - viewStart;
    const step = span * 0.1;
    const shift = dir * step;
    const newStart = Math.max(MIN_YEAR, viewStart + shift);
    const newEnd = Math.min(MAX_YEAR, viewEnd + shift);
    if (newEnd - newStart >= 100) {
      setViewStart(newStart);
      setViewEnd(newEnd);
    }
  };

  const zoom = (factor) => {
    const center = (viewStart + viewEnd) / 2;
    const span = Math.max(100, Math.min(MAX_YEAR - MIN_YEAR, (viewEnd - viewStart) * factor));
    setViewStart(Math.max(MIN_YEAR, center - span / 2));
    setViewEnd(Math.min(MAX_YEAR, center + span / 2));
  };

  /* ─ tooltip content ─ */
  let tooltip = null;
  if (hover?.type === 'chain') {
    const c = hover.data;
    const rating = c.average_rating !== null && c.average_rating !== undefined ? `${c.average_rating}/25` : 'unrated';
    const POP_W = 340;
    const POP_H = 300;
    const canvasW = canvasRef.current?.clientWidth || 1200;
    const canvasH = canvasRef.current?.clientHeight || 240;
    // Smart position: prefer ABOVE the cursor (picture reads above the
    // spine), flip below if no room. Horizontal flip if the card would
    // overrun the right edge.
    const prefTop = hover.mouseY - POP_H - 18;
    const top = prefTop >= 0 ? prefTop : Math.min(hover.mouseY + 18, Math.max(0, canvasH - POP_H));
    const prefLeft = hover.mouseX - POP_W / 2;
    const left = Math.max(8, Math.min(prefLeft, canvasW - POP_W - 8));
    tooltip = (
      <div
        className="absolute pointer-events-none z-40 timeline-chain-card"
        style={{ left: `${left}px`, top: `${top}px`, width: `${POP_W}px` }}
        data-testid="timeline-chain-popover"
      >
        <div className="relative overflow-hidden rounded-lg border border-[#D4A84A]/55 bg-[#0A0A0A]/96 shadow-[0_16px_48px_rgba(212,168,74,0.28),0_0_0_1px_rgba(47,138,146,0.18)]">
          {c.pinhole_opening_url && (
            <div className="relative w-full aspect-[16/9] overflow-hidden">
              <img
                src={c.pinhole_opening_url}
                alt={c.title || c.subject || 'episode'}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                data-testid="timeline-chain-popover-image"
              />
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.96) 0%, rgba(10,10,10,0.35) 55%, transparent 100%)' }} />
              <div className="absolute top-2 left-2 font-hud text-[15px] tracking-[0.3em] uppercase text-[#D4A84A] bg-[#0A0A0A]/70 px-2 py-1 rounded-sm border border-[#D4A84A]/35">
                ⟨ {String(c.era || 'deep_time').replace('_', ' ')} ⟩
              </div>
            </div>
          )}
          <div className="px-4 py-3">
            <div className="text-[#D4A84A] font-historical text-xl leading-tight" data-testid="timeline-chain-popover-title">
              {c.title || c.subject || 'Episode'}
            </div>
            <div className="text-[#2F8A92]/95 text-[16px] tracking-[0.3em] uppercase font-hud mt-1">
              {formatYear(c.origin.year)}
              {c.origin.location ? ` · ${c.origin.location}` : ''}
              {' · '}{rating}
            </div>
            {c.description && (
              <div className="text-white/82 font-historical text-[13px] mt-2 leading-snug tracking-normal normal-case">
                {c.description.length > 180 ? `${c.description.slice(0, 180)}…` : c.description}
              </div>
            )}
            <div className="mt-3 pt-2 border-t border-[#D4A84A]/18 flex items-center justify-between">
              <span className="text-[#D4A84A] font-hud text-[16px] tracking-[0.32em] uppercase">Walk this chain</span>
              <span className="text-[#D4A84A] font-hud text-lg leading-none">→</span>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (hover?.type === 'beacon') {
    const b = hover.data;
    const span = b.birth_year ? `${b.birth_year}${b.death_year ? `–${b.death_year}` : ''}` : '—';
    tooltip = (
      <div
        className="absolute pointer-events-none font-hud text-[16px] tracking-wider bg-[#0A0A0A]/95 border border-[#2F8A92]/30 rounded px-3 py-2 z-20"
        style={{ left: `${hover.mouseX + 10}px`, top: `${Math.max(0, hover.mouseY - 40)}px` }}
        data-testid="timeline-tooltip"
      >
        <div className="text-[#D4A84A] font-historical text-sm">{b.name}</div>
        <div className="text-white/80 mt-0.5">
          {b.relation} · {span} · {b.location || 'unknown'}
        </div>
      </div>
    );
  } else if (hover?.type === 'event') {
    // Hover-based blue-point popover (transient). The pinned variant is
    // rendered below outside this `tooltip` block so it survives
    // mouseLeave.
    const ev = hover.data;
    const era = ev.era || eraForYear(ev.year)?.label?.toLowerCase() || '';
    tooltip = (
      <div
        className="absolute pointer-events-none font-hud text-[16px] tracking-wider bg-[#0A0A0A]/96 border border-[#2F8A92]/45 rounded px-3 py-2 z-30 max-w-[280px]"
        style={{ left: `${Math.min(hover.mouseX + 10, (canvasRef.current?.clientWidth || 800) - 290)}px`, top: `${Math.max(0, hover.mouseY - 50)}px` }}
        data-testid="timeline-event-popover"
      >
        <div className="text-[#D4A84A] font-historical text-sm leading-tight" data-testid="timeline-event-popover-title">
          {ev.title || ev.name || 'Event'}
        </div>
        <div className="text-[#2F8A92]/95 mt-0.5 text-[16px]">
          {formatYear(ev.year)}{era ? ` · ${String(era).toUpperCase()}` : ''}{ev.category ? ` · ${ev.category}` : ''}
        </div>
        {ev.summary && (
          <div className="text-white/80 font-mono text-[16px] mt-1 normal-case tracking-normal leading-snug">
            {ev.summary}
          </div>
        )}
      </div>
    );
  }

  // Pinned event popover — shows after a click on a context-event dot.
  // Survives mouseLeave so the user can click through to the source link.
  // Dismissed by clicking elsewhere on the canvas or the X button below.
  let pinnedPopover = null;
  if (pinnedEvent?.data) {
    const ev = pinnedEvent.data;
    const era = ev.era || eraForYear(ev.year)?.label?.toLowerCase() || '';
    pinnedPopover = (
      <div
        className="absolute font-hud text-[16px] tracking-wider bg-[#0A0A0A]/97 border border-[#D4A84A]/55 rounded px-3 py-2.5 z-40 max-w-[300px] shadow-[0_8px_24px_rgba(0,0,0,0.65)]"
        style={{ left: `${Math.min(pinnedEvent.mouseX + 10, (canvasRef.current?.clientWidth || 800) - 310)}px`, top: `${Math.max(4, pinnedEvent.mouseY - 60)}px` }}
        data-testid="timeline-event-popover-pinned"
      >
        <button
          onClick={() => setPinnedEvent(null)}
          data-testid="timeline-event-popover-close"
          className="absolute top-1 right-1 text-white/55 hover:text-white p-1 leading-none"
          aria-label="Close popover"
        >
          <X size={11} />
        </button>
        <div className="text-[#D4A84A] font-historical text-sm leading-tight pr-5">
          {ev.title || ev.name || 'Event'}
        </div>
        <div className="text-[#2F8A92]/95 mt-0.5 text-[16px]">
          {formatYear(ev.year)}{era ? ` · ${String(era).toUpperCase()}` : ''}{ev.category ? ` · ${ev.category}` : ''}
        </div>
        {ev.summary && (
          <div className="text-white/85 font-mono text-[16px] mt-1.5 normal-case tracking-normal leading-snug">
            {ev.summary}
          </div>
        )}
        {ev.source_url && (
          <a
            href={ev.source_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="timeline-event-popover-source"
            className="block mt-2 text-[#D4A84A]/90 hover:text-[#D4A84A] underline decoration-dotted text-[16px]"
            onClick={(e) => e.stopPropagation()}
          >
            View source ↗
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="w-full select-none" data-testid="chain-timeline">
      {/* ─── Apr 30 2026 iter_67 per Lead: "I asked you to move time and
           play button to actual timeline in timeline!" The top header
           row that held the `< 7M BCE >` year readout + era tag + nav
           arrows has been REMOVED. The year badge now rides above the
           play button at the cursor position (see anchored-play-button
           block below). Only the range + zoom controls remain in a
           compact single-line strip. ─── */}
      <div className="flex items-center justify-end gap-2 mb-1 px-2">
        <div className="flex items-center gap-1 font-hud text-[13px] tracking-wider text-[#52525B]">
          <span>{formatYear(viewStart)} → {formatYear(viewEnd)}</span>
          <button
            data-testid="timeline-back"
            onClick={() => nudge(-1)}
            className="ml-2 text-[#2F8A92]/70 hover:text-[#2F8A92] transition-all"
            title="Back"
          >
            <ChevronLeft size={12} />
          </button>
          <button
            data-testid="timeline-forward"
            onClick={() => nudge(1)}
            className="text-[#2F8A92]/70 hover:text-[#2F8A92] transition-all"
            title="Forward"
          >
            <ChevronRight size={12} />
          </button>
          <button
            data-testid="timeline-zoom-out"
            onClick={() => zoom(1.4)}
            className="ml-2 text-[#2F8A92]/70 hover:text-[#2F8A92] transition-all"
            title="Zoom out"
          >
            <ZoomOut size={11} />
          </button>
          <button
            data-testid="timeline-zoom-in"
            onClick={() => zoom(0.7)}
            className="text-[#2F8A92]/70 hover:text-[#2F8A92] transition-all"
            title="Zoom in"
          >
            <ZoomIn size={11} />
          </button>
          <button
            data-testid="timeline-export-btn"
            onClick={() => setExportOpen(true)}
            className="ml-1 text-[#D4A84A]/70 hover:text-[#D4A84A] transition-all"
            title="Share this timeline"
          >
            <Camera size={11} />
          </button>
        </div>
      </div>

      {/* ─── Canvas ─── */}
      <div
        ref={wrapperRef}
        className="relative"
        style={{ cursor: isDragging ? 'grabbing' : hover ? 'pointer' : 'grab' }}
      >
        <canvas
          ref={canvasRef}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
          onClick={onClick}
          onWheel={onWheel}
          onDoubleClick={onDoubleClick}
          data-testid="timeline-canvas"
        />
        {tooltip}
        {pinnedPopover}
        {/* ─── Journey play button — anchored at the BOTTOM of the gold
             cursor line. Apr 28 2026 Lead brief: "Where the gold is is
             where the play button should be. So since arc are on top
             the put the play button on the bottom of that gold vertical
             line." Position recomputes whenever currentYear or canvas
             width changes, so the button slides along with the cursor
             as the user pans / zooms / scrubs. Clamped to the visible
             range so it stays fully on-screen at deep-time extremes
             (e.g. currentYear = -7,000,000 sits flush at the left edge). */}
        {cursorX >= 0 && (
          <>
            {/* Year badge — rides on the TIMELINE AXIS LINE with the
                play button so time + play read as one unit. Apr 30 2026
                iter_68 per Lead's 3rd repeat of "move up time Dates and
                play button In the timeline." Badge is now anchored at
                `top: calc(50% - 30px)` instead of bottom:34px so it
                sits above the horizontal axis midline, not at the
                bottom of the row. Same for the play button. */}
            <div
              className="absolute z-30 pointer-events-none font-hud text-xs md:text-sm tracking-widest uppercase px-2 py-1 rounded-sm"
              data-testid="timeline-current-year"
              style={{
                left: `${Math.max(6, Math.min(canvasWidth - 120, cursorX - 48))}px`,
                top: 'calc(50% - 40px)',
                color: eraForYear(currentYear).color,
                background: 'rgba(10,10,10,0.92)',
                border: '1px solid rgba(212,168,74,0.35)',
                whiteSpace: 'nowrap',
              }}
            >
              {formatYear(currentYear)}
            </div>
            <button
              data-testid="timeline-play-journey"
              onClick={(e) => {
                e.stopPropagation();
                // Apr 30 2026 iter_68 per Lead: "No effects when you
                // press play." Re-added a pure-CSS screen flash + pulse
                // on the body so the transition reads cinematic even
                // though the old SFX were silenced. Class is removed
                // 720ms later.
                try {
                  if (typeof document !== 'undefined') {
                    document.body.classList.add('animate-timeline-press-flash');
                    setTimeout(() => {
                      document.body.classList.remove('animate-timeline-press-flash');
                    }, 740);
                  }
                } catch { /* ignore */ }
                if (onStartJourney) onStartJourney();
                else if (onChainSelect) onChainSelect({ origin: 'play-journey', year: currentYear });
              }}
              className="absolute z-20 group flex items-center justify-center rounded-full border-2 border-[#D4A84A] bg-[#0A0A0A] hover:bg-[#D4A84A]/20 transition-all duration-200 animate-timeline-play-pulse"
              style={{
                left: `${Math.max(0, Math.min(canvasWidth - 30, cursorX - 15))}px`,
                top: 'calc(50% - 15px)',
                width: '30px',
                height: '30px',
                boxShadow: '0 0 14px rgba(212,168,74,0.65), 0 0 28px rgba(212,168,74,0.28)',
              }}
              title={`Continue your journey from ${formatYear(currentYear)}`}
            >
              <svg width="11" height="13" viewBox="0 0 9 11" fill="none" className="ml-0.5">
                <path d="M8.5 5.5L0.5 10.5V0.5L8.5 5.5Z" fill="#D4A84A" />
              </svg>
            </button>
          </>
        )}
        {showLegend && (
          <TimelineLegend
            onDismiss={() => {
              setShowLegend(false);
              try { localStorage.setItem('tsogn-legend-dismissed', '1'); } catch {}
            }}
          />
        )}
        {!showLegend && (
          <button
            data-testid="timeline-legend-show"
            onClick={() => { setShowLegend(true); try { localStorage.removeItem('tsogn-legend-dismissed'); } catch {} }}
            className="absolute bottom-1 left-2 text-white/50 hover:text-[#2F8A92] transition-all"
            title="Show legend"
          >
            <HelpCircle size={12} />
          </button>
        )}
      </div>

      {exportOpen && (
        <ExportModal
          onClose={() => setExportOpen(false)}
          hasLineage={hasLineage}
          state={{
            viewStart, viewEnd, chains, beacons, intersections, densityGrid, currentYear,
          }}
        />
      )}
    </div>
  );
}

function ExportModal({ onClose, hasLineage, state }) {
  // Defaults per the brief: respect privacy by default. User opts in.
  const [includeLineage, setIncludeLineage] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewCanvasRef = useRef(null);

  const render = useCallback(() => {
    setRendering(true);
    try {
      // Render to a hidden canvas at export resolution (2x DPR-equivalent).
      const W = 1280, H = 396; // 2x CANVAS_H
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      // width accessor used by drawFrame reads canvas.clientWidth, which is 0
      // for detached canvases — wrap it in a DOM element briefly to get a size.
      canvas.style.width = `${W / 2}px`;
      canvas.style.height = `${H / 2}px`;
      // Shim clientWidth via getter since detached elements return 0
      Object.defineProperty(canvas, 'clientWidth', { value: W / 2, configurable: true });
      const ctx = canvas.getContext('2d');
      ctx.setTransform(2, 0, 0, 2, 0, 0);
      drawFrame(canvas, {
        ...state,
        particles: [],
        hover: null,
        dt: 0,
        exportMode: { includeLineage, watermark: true },
      });
      const url = canvas.toDataURL('image/png');
      setPreviewUrl(url);
      previewCanvasRef.current = canvas;
    } finally {
      setRendering(false);
    }
  }, [state, includeLineage]);

  useEffect(() => { render(); }, [render]);

  const download = () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `tsogn-timeline-${stamp}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const body = (
    <div
      className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 10050, isolation: 'isolate' }}
      onClick={onClose}
      data-testid="timeline-export-modal"
    >
      <div
        className="bg-[#0A0A0A] border border-[#D4A84A]/30 rounded-lg max-w-2xl w-full p-5 md:p-6 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-historical text-[#D4A84A] text-xl font-light tracking-tight">Share this timeline</h3>
            <p className="font-mono text-[16px] text-white/80">PNG export · watermarked · privacy-guardrailed</p>
          </div>
          <button
            onClick={onClose}
            data-testid="timeline-export-close"
            className="text-white/80 hover:text-[#D4A84A]"
          >
            <X size={16} />
          </button>
        </div>

        {/* Preview */}
        <div className="border border-[#2F8A92]/15 rounded overflow-hidden bg-[#0F0F10]">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Timeline export preview (includes TSOGN · tsogn.com watermark bottom-right)"
              className="w-full h-auto block"
              data-testid="timeline-export-preview"
            />
          ) : (
            <div className="h-32 flex items-center justify-center font-hud text-[16px] text-white/80 tracking-widest">
              RENDERING…
            </div>
          )}
        </div>
        {/* Machine-readable watermark marker — the visible watermark is canvas-drawn
         *  ("TSOGN · tsogn.com" at bottom-right); this sr-only node mirrors it for
         *  assistive tech + automated tests. */}
        <span className="sr-only" data-testid="timeline-export-watermark-label">
          Watermark: TSOGN · tsogn.com
        </span>

        {/* Privacy toggles */}
        <div className="space-y-2">
          {hasLineage ? (
            <label
              className="flex items-center gap-2 cursor-pointer border border-[#52525B]/20 rounded p-2"
              data-testid="timeline-export-include-lineage"
            >
              <input
                type="checkbox"
                checked={includeLineage}
                onChange={(e) => setIncludeLineage(e.target.checked)}
                className="accent-[#D4A84A]"
              />
              <div>
                <p className="font-hud text-[16px] tracking-wider uppercase text-white/80">Include my lineage ribbons</p>
                <p className="font-mono text-[15px] text-[#52525B]">Off by default. Respects per-ancestor hide toggle. Names are never drawn onto the canvas — only birth-to-death bands.</p>
              </div>
            </label>
          ) : (
            <p className="font-mono text-[16px] text-[#52525B] px-2">No lineage to include (no ancestors added to My Roots yet).</p>
          )}
          <p className="font-mono text-[15px] text-[#52525B] px-2">
            Export always carries a discreet TSOGN watermark. Era colors, chain constellations, and
            density heatmap render identically to what you see on screen.
          </p>
        </div>

        <div className="flex gap-2 justify-end pt-1">
          <button
            onClick={onClose}
            className="font-hud text-xs tracking-wider text-white/80 hover:text-[#2F8A92] px-3 py-2"
          >
            CANCEL
          </button>
          <button
            onClick={download}
            disabled={!previewUrl || rendering}
            data-testid="timeline-export-download"
            className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-60 flex items-center gap-2"
          >
            <Download size={12} /> DOWNLOAD PNG
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return body;
  return createPortal(body, document.body);
}

/* ─── TimelineLegend — tiny inline micro-legend, shows the visual language
 *     of the canvas itself: a gold chain line, a cyan contradiction dash,
 *     an arc-to-today, thick-vs-thin rating weight, a context-event dot.
 *
 *  Positioned top-center inside the timeline container so it sits in the
 *  gap below the globe row, clear of year-tick labels at the bottom (Lead
 *  Phase 1.5 brief: "centered below globe, properly sized, no overlap with
 *  surrounding text"). ─── */
function TimelineLegend({ onDismiss }) {
  return (
    <div
      className="absolute top-0 left-1/2 -translate-x-1/2 bg-[#0A0A0A]/95 border border-white/10 rounded-b-md px-3 md:px-4 py-0.5 md:py-1 flex items-center gap-3 md:gap-4 backdrop-blur-sm whitespace-nowrap"
      data-testid="timeline-legend"
      style={{ fontSize: '17px', zIndex: 5 }}
    >
      {/* Chain line */}
      <span className="flex items-center gap-1 text-white/55 font-mono">
        <svg width="14" height="6"><line x1="0" y1="3" x2="14" y2="3" stroke="#D4A84A" strokeWidth="1.6" /></svg>
        chain
      </span>
      {/* Contradiction */}
      <span className="flex items-center gap-1 text-white/55 font-mono">
        <svg width="14" height="6"><line x1="0" y1="3" x2="14" y2="3" stroke="#2F8A92" strokeWidth="1.2" strokeDasharray="2 2" /></svg>
        contradiction
      </span>
      {/* Arc to today */}
      <span className="flex items-center gap-1 text-white/55 font-mono">
        <svg width="14" height="8"><path d="M 0 6 Q 7 0 14 6" stroke="#D4A84A" strokeWidth="1" fill="none" opacity="0.5" /></svg>
        arc-to-today
      </span>
      {/* Rating line weight */}
      <span className="flex items-center gap-1 text-white/55 font-mono">
        <svg width="18" height="6">
          <line x1="0" y1="3" x2="8" y2="3" stroke="#D4A84A" strokeWidth="0.8" />
          <line x1="10" y1="3" x2="18" y2="3" stroke="#D4A84A" strokeWidth="2.2" />
        </svg>
        unrated→rated
      </span>
      {/* Context event */}
      <span className="flex items-center gap-1 text-white/55 font-mono">
        <svg width="6" height="6"><circle cx="3" cy="3" r="1.6" fill="#2F8A92" /></svg>
        event
      </span>
      <button
        onClick={onDismiss}
        data-testid="timeline-legend-dismiss"
        className="ml-1 text-white/35 hover:text-[#D4A84A]"
        aria-label="Dismiss legend"
      >
        <X size={10} />
      </button>
    </div>
  );
}

/* ─── Canvas drawing — called every frame ─── */
function drawFrame(canvas, state) {
  const ctx = canvas.getContext('2d');
  const width = canvas.clientWidth;
  const height = CANVAS_H;
  ctx.clearRect(0, 0, width, height);

  drawSpineAndEras(ctx, width, state);
  drawDensityHeatmap(ctx, width, state);
  if (!state.exportMode) drawParticles(ctx, width, state);
  if (!state.exportMode || state.exportMode.includeLineage) {
    drawRibbons(ctx, width, state);
    drawIntersections(ctx, width, state);
  }
  drawContextEvents(ctx, width, state);
  drawConstellations(ctx, width, state);
  if (!state.exportMode) drawScrubCursor(ctx, width, state);
  drawYearLabels(ctx, width, state);
  if (state.exportMode?.watermark) drawWatermark(ctx, width, height);
}

function drawWatermark(ctx, width, height) {
  ctx.save();
  ctx.fillStyle = 'rgba(212,168,74,0.75)';
  ctx.font = '17px "IBM Plex Mono", monospace';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText('TSOGN · tsogn.com', width - 8, height - 4);
  ctx.restore();
}

/* ─── Context Event markers — canonical historical anchors on the spine ───
 *
 * Cyan micro-dots BELOW the spine midpoint, distinct from gold Origin nodes
 * ABOVE. Label fades in when the scrub cursor is within ±proximity-year
 * window; dense clusters cascade with 150ms offset for a rhythmic unfold.
 *
 * Note on x-mapping: event dots render at yearToX(ev.year) — strictly linear
 * across [viewStart, viewEnd]. The proximity window is measured in YEARS
 * (±3% of visible span), NOT pixels, so labels attach to the temporally
 * nearest event regardless of pixel density. At extreme zoom-outs where many
 * centuries compress into a narrow band, the visual result is a label
 * selecting the nearest cyan dot near the cursor — intentional.
 */
function drawContextEvents(ctx, width, state) {
  const { contextEvents, viewStart, viewEnd, currentYear, exportMode } = state;
  if (!Array.isArray(contextEvents) || contextEvents.length === 0) return;
  const proximityRange = Math.max(50, (viewEnd - viewStart) * 0.03); // ±3% of visible span
  const y = LAYOUT.spineMid + 10;

  // Precompute visible events with proximity ordering (for cascade)
  const visible = [];
  for (const ev of contextEvents) {
    const x = yearToX(ev.year, viewStart, viewEnd, width);
    if (x < -40 || x > width + 40) continue;
    visible.push({ ev, x });
  }

  // Sort proximity-hits by absolute year-distance so cascade unfolds nearest-first
  const hits = visible
    .map((v) => ({ ...v, dist: Math.abs(v.ev.year - currentYear) }))
    .filter((v) => v.dist <= proximityRange)
    .sort((a, b) => a.dist - b.dist);

  const nowMs = performance.now();

  for (const { ev, x } of visible) {
    const yearDistance = Math.abs(ev.year - currentYear);
    const near = yearDistance <= proximityRange;
    const intensity = near ? 1 - yearDistance / proximityRange : 0.35;

    // Cyan micro-dot — pulse subtly when near
    const pulse = near ? 1 + 0.3 * Math.sin(nowMs / 250 + ev.year) : 1;
    ctx.save();
    const dotAlpha = exportMode ? 0.85 : 0.55 + 0.45 * intensity;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, 5 * pulse);
    grad.addColorStop(0, `rgba(47,138,146,${dotAlpha})`);
    grad.addColorStop(1, 'rgba(47,138,146,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, 5 * pulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(47,138,146,${exportMode ? 0.95 : 0.5 + 0.5 * intensity})`;
    ctx.beginPath();
    ctx.arc(x, y, 1.8 * pulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Cascade labels — each subsequent hit delayed by 150ms so dense clusters unfold
  hits.forEach((h, idx) => {
    const cascadeDelay = idx * 150;
    // In export mode, render instantly; otherwise ease-in based on idx
    const labelAlpha = exportMode ? 0.95 : Math.min(1, (nowMs - cascadeDelay) / 400);
    if (labelAlpha <= 0) return;
    const ev = h.ev;
    const x = h.x;
    const near = h.dist <= proximityRange;
    if (!near && !exportMode) return;
    ctx.save();
    ctx.font = '16px "IBM Plex Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = `rgba(47,138,146,${labelAlpha * 0.9})`;
    const labelY = y + 10;
    // Background ghost for legibility
    const metrics = ctx.measureText(ev.title);
    const boxW = metrics.width + 10;
    ctx.fillStyle = `rgba(10,10,10,${labelAlpha * 0.7})`;
    ctx.fillRect(x - boxW / 2, labelY - 1, boxW, 12);
    ctx.fillStyle = `rgba(47,138,146,${labelAlpha * 0.95})`;
    ctx.fillText(ev.title, x, labelY + 1);
    ctx.restore();
  });
}

function drawSpineAndEras(ctx, width, { viewStart, viewEnd, currentYear }) {
  // Gradient across viewport — era color at each era's center, bleeds
  const grad = ctx.createLinearGradient(0, 0, width, 0);
  ERAS.forEach((era) => {
    const center = (era.range[0] + era.range[1]) / 2;
    const x = (center - viewStart) / (viewEnd - viewStart);
    if (x < -0.3 || x > 1.3) return;
    grad.addColorStop(Math.max(0, Math.min(1, x)), hexToRgba(era.color, 0.28));
  });

  // Spine band
  ctx.fillStyle = grad;
  ctx.fillRect(0, LAYOUT.spineTop, width, LAYOUT.spineHeight);
  // Soft outer border
  ctx.strokeStyle = 'rgba(47,138,146,0.12)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0, LAYOUT.spineTop, width, LAYOUT.spineHeight);

  // Era labels (centered) — only if era is in viewport. Bumped to 15px bold
  // for full-width readability per Demo Day polish (was 13px).
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.font = 'bold 22px "IBM Plex Mono", monospace';
  ERAS.forEach((era) => {
    const center = (era.range[0] + era.range[1]) / 2;
    const x = yearToX(center, viewStart, viewEnd, width);
    if (x < -50 || x > width + 50) return;
    const active = currentYear >= era.range[0] && currentYear <= era.range[1];
    ctx.fillStyle = hexToRgba(era.color, active ? 0.95 : 0.5);
    ctx.fillText(era.label, x, LAYOUT.spineTop + LAYOUT.spineHeight / 2);
  });
}

function drawDensityHeatmap(ctx, width, { viewStart, viewEnd, densityGrid }) {
  if (!densityGrid) return;
  const max = Math.max(1, ...Object.values(densityGrid));
  for (const [binStr, count] of Object.entries(densityGrid)) {
    const bin = parseInt(binStr, 10);
    const x0 = yearToX(bin, viewStart, viewEnd, width);
    const x1 = yearToX(bin + 200, viewStart, viewEnd, width);
    if (x1 < 0 || x0 > width) continue;
    const intensity = count / max;
    ctx.fillStyle = `rgba(212,168,74,${0.04 + intensity * 0.12})`;
    ctx.fillRect(x0, LAYOUT.spineTop, x1 - x0, LAYOUT.spineHeight);
  }
}

function drawParticles(ctx, width, { viewStart, viewEnd, dt, particles }) {
  for (const p of particles) {
    p.x += p.vx * dt;
    if (p.x > 1.1) p.x = -0.1;
    const x = p.x * width;
    ctx.fillStyle = `rgba(47,138,146,${p.alpha})`;
    ctx.beginPath();
    ctx.arc(x, p.y, 0.8, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawRibbons(ctx, width, { viewStart, viewEnd, beacons }) {
  for (const b of beacons) {
    if (!b.birth_year) continue;
    const bStart = b.birth_year;
    const bEnd = b.death_year || (bStart + 85);
    const x0 = yearToX(bStart, viewStart, viewEnd, width);
    const x1 = yearToX(bEnd, viewStart, viewEnd, width);
    if (x1 < 0 || x0 > width) continue;
    const strength = b.strength || 0.4;
    const grad = ctx.createLinearGradient(0, LAYOUT.ribbonTop, 0, LAYOUT.ribbonTop + LAYOUT.ribbonHeight);
    grad.addColorStop(0, `rgba(212,168,74,${0.05 + strength * 0.3})`);
    grad.addColorStop(1, `rgba(212,168,74,${0.02})`);
    ctx.fillStyle = grad;
    ctx.fillRect(x0, LAYOUT.ribbonTop, Math.max(2, x1 - x0), LAYOUT.ribbonHeight);
    // Left + right edges — subtle gold line
    ctx.strokeStyle = `rgba(212,168,74,${0.25 + strength * 0.5})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x0, LAYOUT.ribbonTop);
    ctx.lineTo(x0, LAYOUT.ribbonTop + LAYOUT.ribbonHeight);
    ctx.moveTo(x1, LAYOUT.ribbonTop);
    ctx.lineTo(x1, LAYOUT.ribbonTop + LAYOUT.ribbonHeight);
    ctx.stroke();
    // Name label if wide enough
    if (x1 - x0 > 40) {
      ctx.fillStyle = `rgba(212,168,74,${0.4 + strength * 0.4})`;
      ctx.font = '16px "Unifrakturmaguntia", serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(b.name, (x0 + x1) / 2, LAYOUT.ribbonTop + LAYOUT.ribbonHeight / 2);
    }
  }
}

function drawIntersections(ctx, width, { viewStart, viewEnd, intersections }) {
  const colors = {
    strong: 'rgba(212,168,74,0.55)',
    medium: 'rgba(47,138,146,0.35)',
    weak: 'rgba(163,163,166,0.2)',
  };
  for (const i of intersections) {
    if (!i.step_year) continue;
    const xChain = yearToX(i.step_year, viewStart, viewEnd, width);
    if (xChain < -20 || xChain > width + 20) continue;
    // Project ancestor ribbon center
    // (we already have beacon data but intersection carries ancestor lat — we'll use step_year as chain anchor)
    const anchorXBeacon = yearToX((i.step_year), viewStart, viewEnd, width); // ribbon under same year
    ctx.strokeStyle = colors[i.proximity] || colors.weak;
    ctx.lineWidth = i.proximity === 'strong' ? 1.4 : 0.9;
    ctx.beginPath();
    ctx.moveTo(xChain, LAYOUT.constellationTop + LAYOUT.constellationHeight);
    ctx.quadraticCurveTo(
      xChain, LAYOUT.spineMid,
      anchorXBeacon, LAYOUT.ribbonTop
    );
    ctx.stroke();
  }
}

function drawConstellations(ctx, width, state) {
  const { viewStart, viewEnd, chains, currentYear, hover, tutorial, exportMode } = state;
  const nowMs = performance.now();
  for (const c of chains) {
    const origin = c.origin;
    if (!origin?.year) continue;
    const originX = yearToX(origin.year, viewStart, viewEnd, width);
    if (originX < -80 || originX > width + 80) continue;

    const rating = c.average_rating;
    // Line weight scales with rating (if rated). Unrated → thin muted lines.
    const lineWeight = rating !== null && rating !== undefined
      ? 0.6 + (rating / 25) * 1.8
      : 0.8;
    const lineAlpha = rating !== null && rating !== undefined
      ? 0.3 + (rating / 25) * 0.5
      : 0.25;

    const hovered = hover?.type === 'chain' && hover.data.id === c.id;
    const glow = hovered ? 1 : 0.7;

    // ── Proximity bloom: scrub cursor within ±2 years of origin triggers
    // a breathing highlight + inline era-typography title ──
    const proximity = Math.abs(origin.year - currentYear);
    const proximityWindow = 2 + Math.max(0, (viewEnd - viewStart) / 2000); // wider zoom, wider window
    const proximityT = proximity <= proximityWindow ? 1 - proximity / proximityWindow : 0;

    // ── Tutorial cascade: on first hover, unfold 5 nodes over 1.5s ──
    const tutorialActive = tutorial && tutorial.chainId === c.id;
    const tutProgress = tutorialActive ? Math.min(1, (tutorial.now - tutorial.startedAt) / 1500) : 1;
    const nodeVisible = (stepIdx) => {
      if (!tutorialActive) return 1;
      // Stagger: step k appears at tutProgress >= k/5 and eases up
      const start = stepIdx / 5;
      if (tutProgress < start) return 0;
      return Math.min(1, (tutProgress - start) / (1 / 5));
    };

    // Compute positions for the 5 steps
    const changeAYear = c.change_a?.year || (origin.year + Math.max(10, (TODAY - origin.year) * 0.2));
    const changeBYear = c.change_b?.year || (origin.year + Math.max(20, (TODAY - origin.year) * 0.4));
    const contradictionYear = c.contradiction?.year || (origin.year + Math.max(30, (TODAY - origin.year) * 0.6));
    const modernYear = c.modern_pattern?.year || TODAY;

    const aX = yearToX(changeAYear, viewStart, viewEnd, width);
    const bX = yearToX(changeBYear, viewStart, viewEnd, width);
    const contrX = yearToX(contradictionYear, viewStart, viewEnd, width);
    const modernX = yearToX(modernYear, viewStart, viewEnd, width);

    const originY = LAYOUT.spineMid;
    const aY = LAYOUT.constellationTop + LAYOUT.constellationHeight * 0.5;
    const bY = LAYOUT.constellationTop + LAYOUT.constellationHeight * 0.3;
    const contrY = LAYOUT.constellationTop + LAYOUT.constellationHeight * 0.15;
    const modernY = LAYOUT.constellationTop + LAYOUT.constellationHeight * 0.7;

    // Origin → Change A line
    ctx.strokeStyle = `rgba(212,168,74,${lineAlpha * glow * nodeVisible(1)})`;
    ctx.lineWidth = lineWeight;
    ctx.beginPath();
    ctx.moveTo(originX, originY);
    ctx.lineTo(aX, aY);
    ctx.stroke();

    // Change A → Change B (fades)
    ctx.strokeStyle = `rgba(212,168,74,${lineAlpha * 0.7 * glow * nodeVisible(2)})`;
    ctx.beginPath();
    ctx.moveTo(aX, aY);
    ctx.lineTo(bX, bY);
    ctx.stroke();

    // Change B → Contradiction (cyan, jagged)
    if (c.contradiction || c.has_contradiction) {
      ctx.strokeStyle = `rgba(47,138,146,${0.4 * glow * nodeVisible(3)})`;
      ctx.lineWidth = lineWeight * 0.8;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(bX, bY);
      ctx.lineTo(contrX, contrY);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Origin → Modern Pattern (long arc — pattern spans time)
    ctx.strokeStyle = `rgba(212,168,74,${lineAlpha * 0.4 * glow * nodeVisible(4)})`;
    ctx.lineWidth = lineWeight * 0.7;
    ctx.beginPath();
    ctx.moveTo(originX, originY);
    ctx.quadraticCurveTo(
      (originX + modernX) / 2,
      LAYOUT.constellationTop - 4,
      modernX, modernY
    );
    ctx.stroke();

    // ── Nodes ──
    // Origin: large gold on spine. Proximity triggers breathing bloom.
    const breathing = 1 + 0.25 * Math.sin(nowMs / 250);
    const originRadius = (hovered ? 7 : 5) * (1 + 0.35 * proximityT * breathing);
    const originAlphaMul = nodeVisible(0);
    const originGrad = ctx.createRadialGradient(originX, originY, 0, originX, originY, originRadius * (2 + proximityT));
    originGrad.addColorStop(0, `rgba(212,168,74,${originAlphaMul})`);
    originGrad.addColorStop(0.5, `rgba(212,168,74,${0.6 * originAlphaMul})`);
    originGrad.addColorStop(1, 'rgba(212,168,74,0)');
    ctx.fillStyle = originGrad;
    ctx.beginPath();
    ctx.arc(originX, originY, originRadius * (2 + proximityT), 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(212,168,74,${originAlphaMul})`;
    ctx.beginPath();
    ctx.arc(originX, originY, originRadius, 0, Math.PI * 2);
    ctx.fill();

    // Tiny persistent step number "1" inside origin
    if (!exportMode && (proximityT > 0.05 || hovered || tutorialActive)) {
      ctx.save();
      ctx.font = '13px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = `rgba(10,10,10,${0.9 * originAlphaMul})`;
      ctx.fillText('1', originX, originY);
      ctx.restore();
    }

    // Change A, B
    drawNode(ctx, aX, aY, 3, '#D4A84A', 0.8 * glow * nodeVisible(1));
    drawNode(ctx, bX, bY, 2.5, '#D4A84A', 0.6 * glow * nodeVisible(2));
    if (!exportMode) {
      drawNodeNumber(ctx, aX, aY, '2', 0.7 * nodeVisible(1));
      drawNodeNumber(ctx, bX, bY, '3', 0.65 * nodeVisible(2));
    }

    // Contradiction — cyan, bigger
    if (c.contradiction || c.has_contradiction) {
      drawNode(ctx, contrX, contrY, 3.5, '#2F8A92', 0.8 * glow * nodeVisible(3));
      if (!exportMode) drawNodeNumber(ctx, contrX, contrY, '4', 0.75 * nodeVisible(3), '#0A0A0A');
    }

    // Modern Pattern — pulsing (cyan — canonical, not rainbow-green)
    const t = nowMs / 1000;
    const pulse = 1 + 0.3 * Math.sin(t * 2 + origin.year);
    drawNode(ctx, modernX, modernY, 3.5 * pulse, '#2F8A92', 0.75 * glow * nodeVisible(4));
    if (!exportMode) drawNodeNumber(ctx, modernX, modernY, '5', 0.75 * nodeVisible(4), '#0A0A0A');

    // ── Proximity / hovered / tutorial title label above constellation ──
    const showTitleAlpha = Math.max(
      proximityT * 0.95,
      hovered ? 0.95 : 0,
      tutorialActive ? Math.min(1, tutProgress * 2) : 0
    );
    if (showTitleAlpha > 0.02 && c.title) {
      const titleY = LAYOUT.constellationTop - 8;
      const eraConfig = getEraConfigForYear(origin.year);
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      const fontStack = eraConfig?.fontHistorical || eraConfig?.fontBody || '"Cormorant Garamond", "Georgia", serif';
      ctx.font = `300 22px ${fontStack}`;
      // Soft background pad for legibility
      const titleMetrics = ctx.measureText(c.title);
      const padX = 8, padY = 3;
      const tw = titleMetrics.width + padX * 2;
      ctx.fillStyle = `rgba(10,10,10,${0.7 * showTitleAlpha})`;
      ctx.fillRect(originX - tw / 2, titleY - 16, tw, 18);
      ctx.fillStyle = `rgba(212,168,74,${0.95 * showTitleAlpha})`;
      ctx.fillText(c.title, originX, titleY);

      // Tutorial step labels — "Origin" / "Change" etc. near each node
      if (tutorialActive) {
        ctx.font = '16px "IBM Plex Mono", monospace';
        ctx.textBaseline = 'bottom';
        const labels = [
          { text: 'ORIGIN', x: originX, y: originY - 10, idx: 0 },
          { text: 'CHANGE', x: aX, y: aY - 10, idx: 1 },
          { text: 'CHANGE', x: bX, y: bY - 10, idx: 2 },
          { text: 'CONTRADICTION', x: contrX, y: contrY - 10, idx: 3 },
          { text: 'MODERN PATTERN', x: modernX, y: modernY - 10, idx: 4 },
        ];
        for (const l of labels) {
          const a = nodeVisible(l.idx);
          if (a <= 0) continue;
          ctx.fillStyle = `rgba(10,10,10,${0.8 * a})`;
          const m = ctx.measureText(l.text);
          ctx.fillRect(l.x - m.width / 2 - 3, l.y - 10, m.width + 6, 12);
          ctx.fillStyle = `rgba(47,138,146,${0.95 * a})`;
          ctx.fillText(l.text, l.x, l.y);
        }
      }
      ctx.restore();
    }
  }
}

/* Tiny persistent step number — low-contrast, teaches the 5-step reading order */
function drawNodeNumber(ctx, x, y, n, alpha = 0.7, color = '#0A0A0A') {
  ctx.save();
  ctx.font = '13px "IBM Plex Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = `rgba(${color === '#0A0A0A' ? '10,10,10' : '255,255,255'},${alpha})`;
  ctx.fillText(n, x, y);
  ctx.restore();
}

/* Era-config lookup for canvas typography — uses same source-of-truth palette */
function getEraConfigForYear(year) {
  // Compressed 5-era mapping reused from ERAS above — keep in sync.
  if (year < -500) return { fontHistorical: '"Trajan Pro", "Cinzel", "Georgia", serif' };
  if (year < 500) return { fontHistorical: '"Cinzel", "Georgia", serif' };
  if (year < 1500) return { fontHistorical: '"Cormorant Garamond", "Georgia", serif' };
  if (year < 1800) return { fontHistorical: '"EB Garamond", "Georgia", serif' };
  return { fontHistorical: '"Playfair Display", "Georgia", serif' };
}

function drawNode(ctx, x, y, r, color, alpha = 1) {
  const grad = ctx.createRadialGradient(x, y, 0, x, y, r * 2);
  grad.addColorStop(0, hexToRgba(color, alpha));
  grad.addColorStop(1, hexToRgba(color, 0));
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, r * 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = hexToRgba(color, alpha);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function drawScrubCursor(ctx, width, { viewStart, viewEnd, currentYear }) {
  const x = yearToX(currentYear, viewStart, viewEnd, width);
  if (x < -5 || x > width + 5) return;

  const grad = ctx.createLinearGradient(x, 0, x, CANVAS_H);
  grad.addColorStop(0, 'rgba(212,168,74,0.8)');
  grad.addColorStop(0.5, 'rgba(212,168,74,0.4)');
  grad.addColorStop(1, 'rgba(212,168,74,0.1)');
  ctx.fillStyle = grad;
  ctx.fillRect(x - 1, 0, 2, CANVAS_H);

  // Halo on the spine where the beam crosses
  const haloGrad = ctx.createRadialGradient(x, LAYOUT.spineMid, 0, x, LAYOUT.spineMid, 12);
  haloGrad.addColorStop(0, 'rgba(212,168,74,0.5)');
  haloGrad.addColorStop(1, 'rgba(212,168,74,0)');
  ctx.fillStyle = haloGrad;
  ctx.beginPath();
  ctx.arc(x, LAYOUT.spineMid, 12, 0, Math.PI * 2);
  ctx.fill();
}

function drawYearLabels(ctx, width, { viewStart, viewEnd }) {
  // Adaptive tick interval based on viewport span. Spans up to 7M years
  // need million-year buckets; recent eras drill down to decades.
  const span = viewEnd - viewStart;
  const interval =
    span > 5_000_000 ? 1_000_000 :
    span > 1_000_000 ? 500_000 :
    span > 200_000   ? 100_000 :
    span > 50_000    ? 10_000 :
    span > 10_000    ? 2_000 :
    span > 2_000     ? 500 :
    span > 500       ? 100 :
    span > 100       ? 25 : 10;
  const first = Math.ceil(viewStart / interval) * interval;
  ctx.fillStyle = 'rgba(160,160,170,0.9)';
  ctx.font = '20px "IBM Plex Mono", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  for (let y = first; y <= viewEnd; y += interval) {
    const x = yearToX(y, viewStart, viewEnd, width);
    if (x < 10 || x > width - 10) continue;
    ctx.fillText(formatYear(y), x, LAYOUT.yearLabelY);
    ctx.fillStyle = 'rgba(47,138,146,0.08)';
    ctx.fillRect(x, LAYOUT.spineTop - 2, 1, LAYOUT.spineHeight + 4);
    ctx.fillStyle = 'rgba(160,160,170,0.9)';
  }
}
